import { useState, useCallback, useRef, createContext, useContext, useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X, Copy } from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
  hint?: string;
  correlationId?: string;
  duration: number;
  createdAt: number;
  dismissing?: boolean;
}

interface ToastContextType {
  showToast: (type: ToastType, message: string, options?: { hint?: string; correlationId?: string }) => void;
  showApiError: (error: any, fallbackMessage?: string) => void;
}

const ToastContext = createContext<ToastContextType>({ showToast: () => {}, showApiError: () => {} });

let toastIdCounter = 0;
const MAX_TOASTS = 4;
const DISMISS_MS = 280;

// ── Individual Toast with progress bar ──────────────────────────
function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ToastItem;
  onDismiss: (id: number) => void;
}) {
  const [progress, setProgress] = useState(100);
  const [copied, setCopied] = useState(false);
  const rafRef = useRef<number>(0);

  // Countdown progress bar
  useEffect(() => {
    const start = toast.createdAt;
    const tick = () => {
      const elapsed = Date.now() - start;
      const pct = Math.max(0, 100 - (elapsed / toast.duration) * 100);
      setProgress(pct);
      if (pct > 0) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [toast.createdAt, toast.duration]);

  const copyCorrelationId = (cid: string) => {
    navigator.clipboard.writeText(cid).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  const icons: Record<ToastType, React.ReactNode> = {
    success: <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />,
    error: <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />,
    warning: <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />,
    info: <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />,
  };

  const bgLight: Record<ToastType, string> = {
    success: 'bg-green-50 border-green-200',
    error: 'bg-red-50 border-red-200',
    warning: 'bg-primary-50 border-primary-200',
    info: 'bg-blue-50 border-blue-200',
  };

  const bgDark: Record<ToastType, string> = {
    success: 'dark:bg-green-950/80 dark:border-green-800/50',
    error: 'dark:bg-red-950/80 dark:border-red-800/50',
    warning: 'dark:bg-primary-950/80 dark:border-primary-800/50',
    info: 'dark:bg-blue-950/80 dark:border-blue-800/50',
  };

  const barColor: Record<ToastType, string> = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-primary-500',
    info: 'bg-blue-500',
  };

  return (
    <div
      className={`
        pointer-events-auto flex flex-col rounded-xl border shadow-lg
        backdrop-blur-sm overflow-hidden
        transition-all duration-300 ease-out
        ${toast.dismissing ? 'opacity-0 translate-x-8 scale-95' : 'opacity-100 translate-x-0 scale-100'}
        animate-in slide-in-from-right-5
        ${bgLight[toast.type]} ${bgDark[toast.type]}
      `}
      role="alert"
    >
      <div className="flex items-start gap-3 px-4 py-3">
        {icons[toast.type]}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{toast.message}</p>
          {toast.hint && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{toast.hint}</p>
          )}
          {toast.correlationId && (
            <button
              onClick={() => copyCorrelationId(toast.correlationId!)}
              className="flex items-center gap-1 mt-1.5 text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Korrelations-ID kopieren (für Support)"
            >
              <Copy className="w-2.5 h-2.5" />
              <span>{copied ? 'Kopiert!' : `Ref: ${toast.correlationId}`}</span>
            </button>
          )}
        </div>
        <button
          onClick={() => onDismiss(toast.id)}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0 transition-colors p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/5"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {/* Progress countdown bar */}
      <div className="h-[2px] w-full bg-black/5 dark:bg-white/5">
        <div
          className={`h-full ${barColor[toast.type]} transition-none`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

// ── Provider ────────────────────────────────────────────────────
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const lastToastRef = useRef<{ msg: string; time: number }>({ msg: '', time: 0 });

  const dismiss = useCallback((id: number) => {
    // Animate out first, then remove
    setToasts(prev => prev.map(t => t.id === id ? { ...t, dismissing: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, DISMISS_MS);
  }, []);

  const showToast = useCallback((type: ToastType, message: string, options?: { hint?: string; correlationId?: string }) => {
    // Deduplication: skip if same message within 1 second
    const now = Date.now();
    if (lastToastRef.current.msg === message && now - lastToastRef.current.time < 1000) {
      return;
    }
    lastToastRef.current = { msg: message, time: now };

    const id = ++toastIdCounter;
    const duration = type === 'error' ? 8000 : 4000;

    setToasts(prev => {
      const next = [...prev, {
        id, type, message,
        hint: options?.hint,
        correlationId: options?.correlationId,
        duration,
        createdAt: now,
      }];
      // FIFO eviction: keep only the newest MAX_TOASTS
      if (next.length > MAX_TOASTS) {
        return next.slice(-MAX_TOASTS);
      }
      return next;
    });

    // Auto-dismiss
    setTimeout(() => dismiss(id), duration);
  }, [dismiss]);

  const showApiError = useCallback((error: any, fallbackMessage?: string) => {
    const data = error?.response?.data;
    const message =
      data?.error || fallbackMessage || data?.hint || 'Ein Fehler ist aufgetreten.';
    const hint =
      data?.details ||
      (typeof data?.hint === 'string' && data.hint !== message ? data.hint : undefined);
    const correlationId = data?.correlationId;
    showToast('error', message, hint ? { hint: String(hint), correlationId } : { correlationId });
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, showApiError }}>
      {children}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2.5 pointer-events-none" style={{ maxWidth: '420px' }}>
        {toasts.map(toast => (
          <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

